import React, { useState, useEffect, useMemo } from 'react';
import NPCGridSelector from './NPCGridSelector';
import NPCModelWithPreviewUploader from './NPCModelWithPreviewUploader';
import NPCSelectFromExisting from './NPCSelectFromExisting';
import { ACTION_PREVIEW_EVENT } from '../../actions-preview/ActionPreview';
import { gltfLoaderWithDraco } from '../../../lib/draco-loader/DracoLoader';
import { openNotification } from 'util/helpers';
import {
  Button,
  InputNumber,
  Select,
  Space,
  Flex,
  Divider,
  Tabs,
  Typography,
} from 'antd';
import { NPCTypes, RequiredNPCAnimationsTypes } from '../../../types';
import {
  useCreateUserSceneAssetsMutation,
  FindManyUserSceneAssetsDocument,
  ActionType,
} from '@/components/generated/graphql';
import { ASSET_TYPE } from '@/components/scene/react-three-components/sceneAsset';
import { STUDIO_ID_SUFFIX } from 'util/constants';
import { makeId } from 'util/helpers';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import styles from '../../../event-timeline.module.css';
import type { TabsProps } from 'antd';

const LABELS = {
  MODEL_URL: 'NPC Model URL:',
  SELECT_FROM_INVENTORY: 'Select from My Inventory:',
  UPLOAD_NEW: 'Upload New NPC with Preview:',
  SELECT_SCENE_ASSETS: 'Select from Existing NPCs in scene:',
} as const;

interface SpawnNPCParamsProps {
  nodeId: string;
  params: any;
  selectableAssets: any[];
  setParams: (params: any) => void;
}

const SpawnNPCParams: React.FC<SpawnNPCParamsProps> = ({
  nodeId,
  params,
  setParams,
  selectableAssets,
}) => {
  const router = useRouter();
  const { data: session } = useSession();
  const [modelAnimations, setModelAnimations] = useState<string[]>([]);
  const safeParams = params || {};
  const sceneId = Array.isArray(router.query?.id)
    ? router.query.id[0]
    : router.query?.id;
  const [createAsset] = useCreateUserSceneAssetsMutation();

  const handleNPCSelected = async (
    inventoryItemId: string,
    url: string,
    previewUrl: string,
    name: string
  ): Promise<void> => {
    try {
      const id = await addToSceneAsset({ url, previewImage: previewUrl, name });
      setParams({
        ...safeParams,
        inventoryItemId: inventoryItemId,
        previewImage: previewUrl,
        assetId: id,
      });

      openNotification(
        'Success',
        'Successfully added NPC to the scene.',
        'topRight',
        2,
        'success'
      );
    } catch (err) {
      openNotification(
        'Error',
        'Error during adding asset to the scene.',
        'topRight',
        2,
        'error'
      );
    }
  };

  const tabItems: TabsProps['items'] = [
    {
      key: 'inventory',
      label: 'My Inventory',
      children: (
        <Flex
          gap={16}
          vertical
          style={{ width: '100%' }}
          className={styles.modelSelector}
        >
          <span className={styles.label}>{LABELS.SELECT_FROM_INVENTORY}</span>
          <NPCGridSelector
            onNPCSelected={handleNPCSelected}
            currentNPCId={safeParams.inventoryItemId}
            npcPreviews={{
              [safeParams.assetId]: safeParams.previewImage,
            }}
          />
        </Flex>
      ),
    },
    {
      key: 'upload',
      label: 'Upload',
      children: (
        <Flex
          gap={16}
          vertical
          style={{ width: '100%' }}
          className={styles.modelSelector}
        >
          <span className={styles.label}>{LABELS.UPLOAD_NEW}</span>
          <NPCModelWithPreviewUploader onNPCUploaded={handleNPCSelected} />
        </Flex>
      ),
    },
    {
      key: 'select',
      label: 'Select from scene',
      children: (
        <Flex
          gap={16}
          vertical
          style={{ width: '100%' }}
          className={styles.modelSelector}
        >
          <span className={styles.label}>{LABELS.SELECT_SCENE_ASSETS}</span>
          <NPCSelectFromExisting
            selectableAssets={selectableAssets}
            params={params}
            setParams={setParams}
          />
        </Flex>
      ),
    },
  ];

  function onSelectStopAreaPositionPoint() {
    window.dispatchEvent(
      new CustomEvent('add_position_capture_point', {
        detail: {
          nodeId,
          actionName: 'Stop Following Area',
          propertyName: 'New Stop Following Area',
          propertyKey: 'stopFollowAreaPosition',
        },
      })
    );
  }

  async function addToSceneAsset({
    url,
    previewImage,
    name,
  }: {
    url: string;
    previewImage: string;
    name: string;
  }): Promise<string> {
    const instanceId = makeId(10);
    const newName = `${name}${STUDIO_ID_SUFFIX}${instanceId}`;

    const assetResult = await createAsset({
      variables: {
        data: {
          name: newName,
          isPortal: false,
          description: `Scene ${sceneId} asset ${newName}`,
          type: ASSET_TYPE.GLTF.OTHER.toString(),
          marketId: undefined,
          shopifyMetadata: undefined,
          url: url,
          previewImage: previewImage,
          textureToChange: '',
          showOnMinimap: false,
          textureToChangeRoughness: '',
          textureToChangeMetalness: '',
          textureToChangeNormal: '',
          baseColor: 0xffffff,
          roughness: -1.0,
          metalness: -1.0,
          userId: session?.user.id!,
          isDisabledOnSceneLoad: true,
          metadata: JSON.stringify({}),
          position: {
            set: [0, 0, 0],
          },
          rotation: {
            set: [0, 0, 0],
          },
          scale: {
            set: [1, 1, 1],
          },
          castShadow: true,
          receiveShadow: true,
          userScene: {
            connect: {
              id: sceneId,
            },
          },
        },
      },
      refetchQueries: [FindManyUserSceneAssetsDocument],
    });
    const id = assetResult.data?.createOneUserSceneAssets.id;
    if (!id) {
      throw new Error('Unable to create Asset');
    }
    window.dispatchEvent(new CustomEvent('refetch_selectable_assets'));
    return id;
  }

  function onOpenPreview() {
    window.dispatchEvent(
      new CustomEvent(ACTION_PREVIEW_EVENT, {
        detail: {
          action: {
            type: ActionType.PlayAnimation,
            params: {},
            modelURL: targetObjectURL,
          },
        },
      })
    );
  }

  const targetObjectURL = useMemo(() => {
    if (!params.assetId || !selectableAssets.length) return '';
    let url = '';

    selectableAssets.forEach((asset) => {
      if (asset.props.asset.id === params.assetId) {
        url = asset.props.asset.url;
      }
    });

    return url;
  }, [params.assetId, selectableAssets.length]);

  useEffect(() => {
    if (!targetObjectURL) return;

    gltfLoaderWithDraco.load(targetObjectURL, (gltf) => {
      const animations: string[] = [];

      if (gltf.animations && gltf.animations.length > 0) {
        gltf.animations.forEach((anim) => {
          animations.push(anim.name);
        });
      }

      setModelAnimations(() => animations);
    });
  }, [gltfLoaderWithDraco, targetObjectURL]);

  return (
    <div className={styles.parameterContainer}>
      <Space direction='vertical' style={{ width: '100%' }}>
        <div className={styles.selectNPCType}>
          <h1 className={styles.h1Label}>Select NPC Type</h1>
          <Select
            value={params.npcType}
            onChange={(value) => setParams({ ...params, npcType: value })}
            style={{ width: '100%' }}
          >
            <Select.Option value={NPCTypes.VILLAGER}>Villager</Select.Option>
            <Select.Option value={NPCTypes.FOLLOWABLE}>
              Followable
            </Select.Option>
            <Select.Option value={NPCTypes.CONTROLLABLE}>
              Controllable
            </Select.Option>
          </Select>
        </div>
        {modelAnimations && (
          <Flex className={styles.selectNPCType} vertical gap={10}>
            <h1 className={styles.h1Label}>Map NPC Animations</h1>
            <Button
              onClick={onOpenPreview}
              className={styles.previewActionButton}
              style={{ marginBottom: '12px' }}
            >
              Preview Animations
            </Button>
            {Object.keys(RequiredNPCAnimationsTypes).map((anim) => {
              return (
                <Flex key={anim} gap={16}>
                  <Typography.Text strong style={{ width: '50%' }}>
                    {anim}:
                  </Typography.Text>
                  <Select
                    value={params?.animations?.[anim]}
                    onChange={(value) =>
                      setParams({
                        ...params,
                        animations: {
                          ...params.animations,
                          [anim]: value,
                        },
                      })
                    }
                    style={{ width: '100%' }}
                  >
                    {modelAnimations.map((animName, i) => {
                      return (
                        <Select.Option value={animName} key={`anim-${i}`}>
                          {animName}
                        </Select.Option>
                      );
                    })}
                  </Select>
                </Flex>
              );
            })}
          </Flex>
        )}
        {params.npcType === NPCTypes.FOLLOWABLE && (
          <Space direction='vertical' style={{ width: '100%' }}>
            <h1 className={styles.h1Label}>
              <span className={styles.buttonLabel}>
                Stop Following NPC Area Center Position
              </span>
              <Button
                className={styles.selectPosButton}
                onClick={onSelectStopAreaPositionPoint}
              >
                Select Point
              </Button>
            </h1>
            <span className={styles.label}>Position X:</span>
            <InputNumber
              value={params.stopFollowAreaPosition?.x || 0}
              onChange={(value) =>
                setParams({
                  ...params,
                  stopFollowAreaPosition: {
                    ...params.stopFollowAreaPosition,
                    x: value,
                  },
                })
              }
              style={{ width: '100%' }}
            />
            <span className={styles.label}>Position Y:</span>
            <InputNumber
              value={params.stopFollowAreaPosition?.y || 0}
              onChange={(value) =>
                setParams({
                  ...params,
                  stopFollowAreaPosition: {
                    ...params.stopFollowAreaPosition,
                    y: value,
                  },
                })
              }
              style={{ width: '100%' }}
            />
            <span className={styles.label}>Position Z:</span>
            <InputNumber
              value={params.stopFollowAreaPosition?.z || 0}
              onChange={(value) =>
                setParams({
                  ...params,
                  stopFollowAreaPosition: {
                    ...params.stopFollowAreaPosition,
                    z: value,
                  },
                })
              }
              style={{ width: '100%' }}
            />
          </Space>
        )}
        <Divider />
        <h1 className={styles.h1Label}>Select or Upload NPC 3D Model</h1>
        <Tabs defaultActiveKey='inventory' items={tabItems} />
      </Space>
    </div>
  );
};

export default SpawnNPCParams;
